/**
 * In-app HERO primitives (the heavier building blocks used inside the product
 * mock window): sidebar nav row, spec-sheet card, and the floating "channels"
 * callout. Styling is 1:1 with the design map §5. Split from
 * `hero-primitives.tsx` to keep each file under the line budget.
 */
import { Pencil } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { CARD_BORDER, H } from "./hero-tokens";

/**
 * Sidebar nav row (flex icon + label, optional trailing count). Active rows are
 * accent-colored on an `accentSoft` background; inactive icons are grey.
 */
export function SidebarRow(props: {
  readonly icon?: ReactNode;
  readonly label: ReactNode;
  readonly active?: boolean;
  readonly count?: number;
  readonly onClick?: () => void;
  readonly style?: CSSProperties;
}): ReactNode {
  const active = props.active === true;
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        appearance: "none",
        border: "none",
        cursor: props.onClick ? "pointer" : "default",
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 9px",
        borderRadius: 8,
        fontFamily: H.sans,
        fontSize: 13.5,
        fontWeight: active ? 600 : 400,
        color: active ? H.accent : H.inkSoft,
        background: active ? H.accentSoft : "transparent",
        textAlign: "left",
        ...props.style,
      }}
    >
      {props.icon !== undefined ? (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            color: active ? H.accent : "#6B7280",
            flexShrink: 0,
          }}
        >
          {props.icon}
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }}>{props.label}</span>
      {props.count !== undefined ? (
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: active ? H.accent : H.inkDim,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {props.count}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Spec-sheet card — a matte card with a mono header bar (label + optional edit
 * pencil) divided from the body by a hairline rule.
 */
export function SpecCard(props: {
  readonly title: ReactNode;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly style?: CSSProperties;
}): ReactNode {
  return (
    <div
      style={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
        ...props.style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 16px",
          borderBottom: "1px solid #F1F2F4",
        }}
      >
        <span
          style={{
            fontFamily: H.label,
            fontSize: 9.5,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: H.inkGhost,
          }}
        >
          {props.title}
        </span>
        {props.action ?? (
          <Pencil width={13} height={13} color={H.inkDim} strokeWidth={2} />
        )}
      </div>
      <div style={{ padding: "13px 16px" }}>{props.children}</div>
    </div>
  );
}

/**
 * Floating "channels" callout — a small white card with a dark mono tab label
 * sticking out of the top. Used for the Inbox "Kanäle" / Serienbrief
 * "Versandkanäle" overlays.
 */
export function FloatingCallout(props: {
  readonly tabLabel: ReactNode;
  readonly children: ReactNode;
  readonly style?: CSSProperties;
}): ReactNode {
  return (
    <div
      style={{
        position: "relative",
        width: 216,
        background: "#fff",
        borderRadius: 15,
        border: `1px solid ${CARD_BORDER}`,
        boxShadow:
          "0 36px 72px -22px rgba(11,15,25,0.46), 0 12px 26px -12px rgba(11,15,25,0.26)",
        padding: "15px 14px 12px",
        ...props.style,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: -11,
          left: 14,
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: H.ink,
          color: "#fff",
          fontFamily: H.label,
          fontSize: 9,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          padding: "4px 9px",
          borderRadius: 7,
          boxShadow: "0 6px 14px -4px rgba(11,15,25,0.5)",
        }}
      >
        {props.tabLabel}
      </span>
      {props.children}
    </div>
  );
}

/**
 * Circular avatar with initials. Defaults to a dark (#0B0F19) circle with white
 * text; pass `color` for a tinted background.
 */
export function Avatar(props: {
  readonly initials: string;
  readonly color?: string;
  readonly size?: number;
}): ReactNode {
  const size = props.size ?? 28;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: props.color ?? H.ink,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontFamily: H.sans,
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
        letterSpacing: "0.02em",
      }}
    >
      {props.initials}
    </span>
  );
}

/** Small round status dot (presence / connection state). */
export function StatusDot(props: {
  readonly color: string;
  readonly size?: number;
}): ReactNode {
  const size = props.size ?? 8;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: props.color,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}
